import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ContextualMenu,
  CONTEXTUAL_MENU_PREVIEW_STATE_LABELS,
  CONTEXTUAL_MENU_PREVIEW_STATES,
  type ContextualMenuItem,
  type ContextualMenuPreviewState,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './ContextualMenuShowcase.css'

const ContextualMenuShowcase: React.FC = () => {
  const [itemCount, setItemCount] = useState(3)
  const [itemLabelPrefix, setItemLabelPrefix] = useState('Item')
  const [highlightedPreviewState, setHighlightedPreviewState] =
    useState<ContextualMenuPreviewState>('default')
  const [lastSelectedItem, setLastSelectedItem] = useState<string | null>(null)
  const cellRefs = useRef<
    Partial<Record<ContextualMenuPreviewState, HTMLDivElement | null>>
  >({})

  const items: ContextualMenuItem[] = useMemo(() => {
    const count = Math.max(1, Math.min(6, itemCount))
    return Array.from({ length: count }, (_, index) => {
      const label = `${itemLabelPrefix} ${index + 1}`
      const id = `item-${index + 1}`

      // Item 2 carries the nested submenu (expanded / on hover).
      if (index === 1) {
        return {
          id,
          label,
          items: [1, 2, 3].map((subIndex) => ({
            id: `${id}-sub-${subIndex}`,
            label: `Sub Item no. ${subIndex}`,
            onSelect: () => setLastSelectedItem(`Sub Item no. ${subIndex}`),
          })),
        }
      }

      return {
        id,
        label,
        onSelect: () => setLastSelectedItem(label),
      }
    })
  }, [itemCount, itemLabelPrefix])

  useEffect(() => {
    cellRefs.current[highlightedPreviewState]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [highlightedPreviewState])

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'contextual-menu-highlight-state',
            label: 'Highlighted state',
            control: (
              <select
                id="contextual-menu-highlight-state"
                className="ui-field-control"
                value={highlightedPreviewState}
                onChange={(event) =>
                  setHighlightedPreviewState(
                    event.target.value as ContextualMenuPreviewState,
                  )
                }
              >
                {CONTEXTUAL_MENU_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {CONTEXTUAL_MENU_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
        ],
      },
      {
        title: 'Content',
        fields: [
          {
            id: 'contextual-menu-item-prefix',
            label: 'Item label prefix',
            control: (
              <input
                id="contextual-menu-item-prefix"
                className="ui-field-control"
                type="text"
                value={itemLabelPrefix}
                onChange={(event) => setItemLabelPrefix(event.target.value)}
              />
            ),
          },
          {
            id: 'contextual-menu-item-count',
            label: 'Item count',
            control: (
              <input
                id="contextual-menu-item-count"
                className="ui-field-control"
                type="number"
                min={1}
                max={6}
                value={itemCount}
                onChange={(event) =>
                  setItemCount(Number(event.target.value) || 1)
                }
              />
            ),
          },
        ],
      },
    ],
    [highlightedPreviewState, itemCount, itemLabelPrefix],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Contextual menu"
        description="Compact kebab trigger with a dropdown list of contextual actions, including nested submenus."
        stageClassName="ui-preview__stage--wide contextual-menu-showcase__stage-wrap"
      >
        <div className={`contextual-menu-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Click the trigger to open the menu. Hover Item 2 to open its
              submenu.
            </p>
            <ContextualMenu items={items} ariaLabel="Actions menu" />
            {lastSelectedItem ? (
              <p className="contextual-menu-showcase__selection-note">
                Selected: {lastSelectedItem}
              </p>
            ) : null}
          </section>

          <div className="contextual-menu-showcase__matrix-header">
            <span className="contextual-menu-showcase__matrix-corner">
              Trigger
            </span>
            {CONTEXTUAL_MENU_PREVIEW_STATES.map((previewState) => (
              <span
                key={previewState}
                className="contextual-menu-showcase__matrix-header-cell"
              >
                {CONTEXTUAL_MENU_PREVIEW_STATE_LABELS[previewState]}
              </span>
            ))}
          </div>

          <div className={`contextual-menu-showcase__matrix-row`}>
            <span className="contextual-menu-showcase__matrix-row-label">
              Menu
            </span>
            {CONTEXTUAL_MENU_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                ref={(element) => {
                  cellRefs.current[previewState] = element
                }}
                className={`contextual-menu-showcase__matrix-cell${
                  previewState === 'expanded'
                    ? ' contextual-menu-showcase__matrix-cell--expanded'
                    : ''
                }${
                  previewState === highlightedPreviewState
                    ? ' contextual-menu-showcase__matrix-cell--selected'
                    : ''
                }`}
              >
                <ContextualMenu
                  items={items.slice(0, 3)}
                  previewState={previewState}
                  ariaLabel={`${CONTEXTUAL_MENU_PREVIEW_STATE_LABELS[previewState]} menu`}
                />
              </div>
            ))}
          </div>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Contextual menu"
        sections={propertySections}
      />
    </>
  )
}

export default ContextualMenuShowcase
